import { FC, PropsWithChildren, useState } from "react";
import { Popover, UnstyledButton } from "@mantine/core";
import { Carousel } from "@mantine/carousel";
import { useTranslation } from "react-i18next";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { OrderTimeSlot } from "@/types";
import OrderDetailCard from "./OrderDetailCard";
import { classNames } from "@/utils/Classnames";
import styles from "./styles.module.css";

interface Props extends PropsWithChildren {
  orders: OrderTimeSlot[];
  ariaLabel: string;
  className?: string;
}

/** Wraps a calendar cell in a click-to-open popover listing the orders booked there — a carousel
 * lets you page through when more than one order shares the same day/slot. */
export const OrdersPopover: FC<Props> = ({ orders, ariaLabel, className, children }) => {
  const [opened, setOpened] = useState(false);
  const { t } = useTranslation();

  if (orders.length === 0) {
    return <>{children}</>;
  }

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-start" withArrow arrowSize={10} shadow="md">
      <Popover.Target>
        <UnstyledButton
          onClick={() => setOpened((o) => !o)}
          aria-label={ariaLabel}
          className={classNames(styles.cellButton, className ?? "")}
        >
          {children}
        </UnstyledButton>
      </Popover.Target>

      <Popover.Dropdown className={styles.popoverDropdown} w={300} p={0}>
        {orders.length === 1 ? (
          <OrderDetailCard order={orders[0]} />
        ) : (
          <Carousel
            withControls
            withIndicators
            classNames={{
              root: styles.carouselRoot,
              control: styles.carouselControl,
              controls: styles.carouselControls,
              indicators: styles.carouselIndicators,
              indicator: styles.carouselIndicator,
            }}
            previousControlIcon={
              <SVGAsset name="chevron-right" width={12} height={12} style={{ transform: "rotate(180deg)" }} />
            }
            nextControlIcon={<SVGAsset name="chevron-right" width={12} height={12} />}
            previousControlProps={{ "aria-label": t("orders:orderCalendarPopover.prevOrder") }}
            nextControlProps={{ "aria-label": t("orders:orderCalendarPopover.nextOrder") }}
          >
            {orders.map((order) => (
              <Carousel.Slide key={order.orderId}>
                <OrderDetailCard order={order} />
              </Carousel.Slide>
            ))}
          </Carousel>
        )}
      </Popover.Dropdown>
    </Popover>
  );
};

export default OrdersPopover;
